/**
 * MobileEvaluationsQuestionnaireTab
 *
 * Phone layout for Settings → "Questionnaire d'évaluations".
 *
 * The desktop shell is a master/detail: evaluation TYPES on the left, the selected type's
 * QUESTIONS on the right. Stacked at 360px that becomes two full-height PrimeReact tables
 * one under the other — you scroll past every type to reach the questions, and the
 * relationship between the two lists disappears entirely. So the phone gets a drill-down
 * instead: a list of types, and tapping one replaces the screen with that type's questions
 * plus a back bar naming the type you are in.
 *
 * Reordering: PrimeReact's `reorderableRows` uses HTML5 drag-and-drop, which does not fire
 * on touch, so ordering is simply unavailable on a phone. Up/down buttons here call the
 * SAME `onReorderTypes` / `onReorderFields` callbacks with the same `{ value: orderedArray }`
 * payload the drag handler produces, so the dispatch and the saved payload are unchanged.
 *
 * Presentational only: every handler and every piece of data is a prop. The container
 * (SettingsView) keeps ownership of Redux.
 */
import React from "react";
import { Box, Typography, Chip, Button, IconButton } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import Langs, { Localize } from "../../../app/lang/langs";

const ORANGE = "rgb(255,144,21)";
const BLUE = "rgb(0,40,128)";

/* A 44x44 tap target, the minimum that is reliably hittable with a thumb. */
const touchButtonSx = (tint) => ({
    width: 44,
    height: 44,
    flexShrink: 0,
    borderRadius: "12px",
    color: tint,
    backgroundColor: "rgba(0,40,128,0.04)",
    border: "1px solid rgba(0,40,128,0.08)",
    "&:active": { transform: "scale(0.94)" },
    "&.Mui-disabled": { opacity: 0.35, color: tint },
    "@media (prefers-reduced-motion: reduce)": { "&:active": { transform: "none" } },
});

const cardSx = {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    backgroundColor: "#fff",
    borderRadius: "14px",
    border: "1px solid rgba(0,40,128,0.07)",
    boxShadow: "0 1px 3px rgba(9,30,66,0.06), 0 2px 10px rgba(95,106,126,0.05)",
    px: 1.5,
    py: 1.25,
};

const sectionTitleSx = {
    fontFamily: "roboto, sans-serif",
    fontSize: "17px",
    fontWeight: 700,
    lineHeight: 1.25,
    color: BLUE,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
};

const sectionDescSx = {
    fontFamily: "roboto, sans-serif",
    fontSize: "13px",
    lineHeight: 1.5,
    color: "rgba(45,42,38,0.62)",
};

const primaryButtonSx = {
    minHeight: 48,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "15px",
    backgroundColor: ORANGE,
    boxShadow: "none",
    "&:hover": { backgroundColor: "#e07600", boxShadow: "none" },
    "&:active": { backgroundColor: "#e07600" },
};

/**
 * Swap `index` with its neighbour. Returns null when the move would fall off the list,
 * so the caller can disable the control instead of dispatching a no-op reorder.
 */
function moveItem(list, index, delta) {
    const target = index + delta;
    if (!Array.isArray(list) || target < 0 || target >= list.length) return null;
    const next = [...list];
    const held = next[index];
    next[index] = next[target];
    next[target] = held;
    return next;
}

function EmptyState({ icon, title, description }) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 1,
                px: 2,
                py: 5,
            }}
        >
            <Box
                aria-hidden="true"
                sx={{
                    width: 52,
                    height: 52,
                    mb: 0.5,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0,40,128,0.05)",
                    color: "rgba(0,40,128,0.35)",
                }}
            >
                {icon}
            </Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "rgba(45,42,38,0.75)" }}>
                {title}
            </Typography>
            <Typography sx={{ fontSize: "13px", lineHeight: 1.5, color: "rgba(45,42,38,0.55)" }}>
                {description}
            </Typography>
        </Box>
    );
}

/** Reorder pair shared by both lists. */
function ReorderControls({ index, count, onMove, labelUp, labelDown }) {
    return (
        <>
            <IconButton
                aria-label={labelUp}
                title={labelUp}
                disabled={index <= 0}
                onClick={() => onMove(-1)}
                sx={touchButtonSx("rgba(0,40,128,0.7)")}
            >
                <KeyboardArrowUpRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <IconButton
                aria-label={labelDown}
                title={labelDown}
                disabled={index >= count - 1}
                onClick={() => onMove(1)}
                sx={touchButtonSx("rgba(0,40,128,0.7)")}
            >
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
        </>
    );
}

export default function MobileEvaluationsQuestionnaireTab({
    types = [],
    fields = [],
    selectedType = null,
    canEdit = false,
    onSelectType,
    onBack,
    onAddType,
    onEditType,
    onEditField,
    onDeleteField,
    onReorderTypes,
    onReorderFields,
    onShowAddRow,
    addRow = null,
    addRowVisible = false,
}) {
    const typeList = Array.isArray(types) ? types : [];
    const fieldList = Array.isArray(fields) ? fields : [];

    const moveLabelUp = Localize("SETTINGS_EVAL_MOVE_UP");
    const moveLabelDown = Localize("SETTINGS_EVAL_MOVE_DOWN");

    const handleMoveType = (index, delta) => {
        const next = moveItem(typeList, index, delta);
        if (next) onReorderTypes({ value: next });
    };

    const handleMoveField = (index, delta) => {
        const next = moveItem(fieldList, index, delta);
        if (next) onReorderFields({ value: next });
    };

    /* ------------------------------------------------------------------ */
    /*  Level 2 — the questions of one type                                */
    /* ------------------------------------------------------------------ */
    if (selectedType) {
        const teams = Array.isArray(selectedType.associated_teams) ? selectedType.associated_teams : [];
        return (
            <Box sx={{ width: "100%", minWidth: 0, pb: "calc(24px + env(safe-area-inset-bottom))" }}>
                {/* Back bar — names the type you are inside, which is the only thing
                    tying these questions to a type once the list is off screen. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                    <IconButton
                        aria-label={Localize("SETTINGS_EVAL_BACK_TO_TYPES")}
                        title={Localize("SETTINGS_EVAL_BACK_TO_TYPES")}
                        onClick={onBack}
                        sx={touchButtonSx(BLUE)}
                    >
                        <ArrowBackRoundedIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ ...sectionDescSx, fontSize: "11.5px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            <Langs str="TYPE" />
                        </Typography>
                        <Typography sx={{ ...sectionTitleSx, whiteSpace: "nowrap" }}>
                            {selectedType.title}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mb: 1.5 }}>
                    <Chip
                        size="small"
                        label={<Langs str={Number(selectedType.active) === 1 ? "ACTIVE" : "INACTIVE"} />}
                        sx={{
                            height: 24,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: Number(selectedType.active) === 1 ? "#1b6b34" : "#8a2c1c",
                            backgroundColor: Number(selectedType.active) === 1 ? "rgba(27,107,52,0.10)" : "rgba(138,44,28,0.10)",
                        }}
                    />
                    <Chip
                        size="small"
                        label={
                            teams.length === 0 ? (
                                <Langs str="SETTINGS_EVAL_TYPE_ALL_TEAMS" />
                            ) : teams.length === 1 ? (
                                <Langs str="SETTINGS_EVAL_TYPE_TEAMS_ONE" params={{ count: teams.length }} />
                            ) : (
                                <Langs str="SETTINGS_EVAL_TYPE_TEAMS_MANY" params={{ count: teams.length }} />
                            )
                        }
                        sx={{
                            height: 24,
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "rgba(0,40,128,0.75)",
                            backgroundColor: "rgba(0,40,128,0.07)",
                        }}
                    />
                </Box>

                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 0.5 }}>
                    <Typography component="h2" sx={sectionTitleSx}>
                        <Langs str="QUESTIONS" />
                    </Typography>
                    {fieldList.length > 0 && (
                        <Typography sx={{ ...sectionDescSx, fontWeight: 700, color: BLUE }}>
                            {fieldList.length}
                        </Typography>
                    )}
                </Box>
                <Typography sx={{ ...sectionDescSx, mb: 1.5 }}>
                    <Langs str="SETTINGS_EVAL_QUESTIONS_DESC_MOBILE" />
                </Typography>

                {canEdit && !addRowVisible && (
                    <Button
                        fullWidth
                        variant="contained"
                        disableElevation
                        startIcon={<AddRoundedIcon />}
                        onClick={onShowAddRow}
                        sx={{ ...primaryButtonSx, mb: 1.5 }}
                    >
                        <Langs str="SETTINGS_EVAL_ADD_QUESTION" />
                    </Button>
                )}

                {addRowVisible && <Box sx={{ mb: 1.5, borderRadius: "14px", overflow: "hidden" }}>{addRow}</Box>}

                {fieldList.length === 0 ? (
                    <Box sx={{ ...cardSx, px: 0, py: 0 }}>
                        <EmptyState
                            icon={<FormatListBulletedRoundedIcon />}
                            title={<Langs str="SETTINGS_EVAL_NO_QUESTIONS_TITLE" />}
                            description={<Langs str="SETTINGS_EVAL_NO_QUESTIONS_DESC" />}
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {fieldList.map((field, index) => (
                            <Box key={field.id} sx={{ ...cardSx, ...(Number(field.active) !== 1 && { opacity: 0.6 }) }}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
                                    <Typography
                                        component="span"
                                        aria-hidden="true"
                                        sx={{
                                            flexShrink: 0,
                                            mt: "2px",
                                            width: 22,
                                            fontSize: "12px",
                                            fontWeight: 700,
                                            color: "rgba(0,40,128,0.4)",
                                        }}
                                    >
                                        {index + 1}
                                    </Typography>
                                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: "15px", fontWeight: 600, color: "rgba(0,0,0,0.82)", wordBreak: "break-word" }}>
                                        {field.title}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                                    {Number(field.team_impact) === 1 && (
                                        <Chip
                                            size="small"
                                            label={<Langs str="EVAL_FIELD_TEAM_IMPACT" />}
                                            title={Localize("EVAL_FIELD_TEAM_IMPACT_HINT")}
                                            sx={{
                                                height: 24,
                                                fontSize: "12px",
                                                fontWeight: 600,
                                                color: "#8a5200",
                                                backgroundColor: "rgba(255,144,21,0.16)",
                                                border: "1px solid rgba(255,144,21,0.35)",
                                            }}
                                        />
                                    )}
                                    {Number(field.active) !== 1 && (
                                        <Chip
                                            size="small"
                                            label={<Langs str="INACTIVE" />}
                                            sx={{ height: 24, fontSize: "12px", fontWeight: 600, color: "#8a2c1c", backgroundColor: "rgba(138,44,28,0.10)" }}
                                        />
                                    )}

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
                                        <ReorderControls
                                            index={index}
                                            count={fieldList.length}
                                            onMove={(delta) => handleMoveField(index, delta)}
                                            labelUp={moveLabelUp}
                                            labelDown={moveLabelDown}
                                        />
                                        <IconButton
                                            aria-label={Localize("EDIT")}
                                            title={Localize("EDIT")}
                                            onClick={() => onEditField(field.id)}
                                            sx={{
                                                ...touchButtonSx(ORANGE),
                                                backgroundColor: "rgba(255,144,21,0.08)",
                                                border: "1px solid rgba(255,144,21,0.2)",
                                            }}
                                        >
                                            <EditRoundedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                        {canEdit && (
                                            /* Pushed away from Edit: a destructive action sitting a few
                                               pixels from a safe one is a mis-tap waiting to happen. */
                                            <IconButton
                                                aria-label={Localize("DELETE")}
                                                title={Localize("DELETE")}
                                                onClick={() => onDeleteField(field.id)}
                                                sx={{
                                                    ...touchButtonSx("#c62828"),
                                                    ml: 1,
                                                    backgroundColor: "rgba(198,40,40,0.06)",
                                                    border: "1px solid rgba(198,40,40,0.18)",
                                                }}
                                            >
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        );
    }

    /* ------------------------------------------------------------------ */
    /*  Level 1 — the list of types                                        */
    /* ------------------------------------------------------------------ */
    return (
        <Box sx={{ width: "100%", minWidth: 0, pb: "calc(24px + env(safe-area-inset-bottom))" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Typography component="h2" sx={sectionTitleSx}>
                    <Langs str="TYPES" />
                </Typography>
                {typeList.length > 0 && (
                    <Typography sx={{ ...sectionDescSx, fontWeight: 700, color: BLUE }}>{typeList.length}</Typography>
                )}
            </Box>
            <Typography sx={{ ...sectionDescSx, mt: 0.25, mb: 1.5 }}>
                <Langs str="SETTINGS_EVAL_TYPES_DESC_MOBILE" />
            </Typography>

            <Button
                fullWidth
                variant="contained"
                disableElevation
                startIcon={<AddRoundedIcon />}
                onClick={onAddType}
                sx={{ ...primaryButtonSx, mb: 1.5 }}
            >
                <Langs str="SETTINGS_EVAL_ADD_TYPE" />
            </Button>

            {typeList.length === 0 ? (
                <Box sx={{ ...cardSx, px: 0, py: 0 }}>
                    <EmptyState
                        icon={<ChecklistRoundedIcon />}
                        title={<Langs str="SETTINGS_EVAL_TYPES_EMPTY_TITLE" />}
                        description={<Langs str="SETTINGS_EVAL_TYPES_EMPTY_DESC" />}
                    />
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {typeList.map((type, index) => {
                        const teams = Array.isArray(type.associated_teams) ? type.associated_teams : [];
                        return (
                            <Box key={type.id} sx={{ ...cardSx, ...(Number(type.active) !== 1 && { opacity: 0.65 }) }}>
                                {/* The whole title row opens the type's questions. It is a real
                                    button so it is reachable by keyboard and announced as an
                                    action, not as decorative text. */}
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => onSelectType(type)}
                                    aria-label={`${Localize("SETTINGS_EVAL_OPEN_QUESTIONS")} — ${type.title}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        width: "100%",
                                        minWidth: 0,
                                        minHeight: 44,
                                        px: 0,
                                        py: 0.5,
                                        border: "none",
                                        background: "transparent",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        "&:active": { opacity: 0.6 },
                                    }}
                                >
                                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: "16px", fontWeight: 700, color: BLUE, wordBreak: "break-word" }}>
                                        {type.title}
                                    </Typography>
                                    <ChevronRightRoundedIcon aria-hidden="true" sx={{ flexShrink: 0, color: "rgba(0,40,128,0.35)" }} />
                                </Box>

                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
                                    <Chip
                                        size="small"
                                        label={<Langs str={Number(type.active) === 1 ? "ACTIVE" : "INACTIVE"} />}
                                        sx={{
                                            height: 24,
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: Number(type.active) === 1 ? "#1b6b34" : "#8a2c1c",
                                            backgroundColor: Number(type.active) === 1 ? "rgba(27,107,52,0.10)" : "rgba(138,44,28,0.10)",
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        label={
                                            teams.length === 0 ? (
                                                <Langs str="SETTINGS_EVAL_TYPE_ALL_TEAMS" />
                                            ) : teams.length === 1 ? (
                                                <Langs str="SETTINGS_EVAL_TYPE_TEAMS_ONE" params={{ count: teams.length }} />
                                            ) : (
                                                <Langs str="SETTINGS_EVAL_TYPE_TEAMS_MANY" params={{ count: teams.length }} />
                                            )
                                        }
                                        sx={{
                                            height: 24,
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: "rgba(0,40,128,0.75)",
                                            backgroundColor: "rgba(0,40,128,0.07)",
                                        }}
                                    />

                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
                                        <ReorderControls
                                            index={index}
                                            count={typeList.length}
                                            onMove={(delta) => handleMoveType(index, delta)}
                                            labelUp={moveLabelUp}
                                            labelDown={moveLabelDown}
                                        />
                                        <IconButton
                                            aria-label={Localize("EDIT")}
                                            title={Localize("EDIT")}
                                            onClick={() => onEditType(type.id)}
                                            sx={{
                                                ...touchButtonSx(ORANGE),
                                                backgroundColor: "rgba(255,144,21,0.08)",
                                                border: "1px solid rgba(255,144,21,0.2)",
                                            }}
                                        >
                                            <EditRoundedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
